import { datadogRum, type RumBeforeSend } from '@datadog/browser-rum';
import { isThrownByNonAppScript } from './thirdPartyErrors';

const KEY = 'dd_rum_renew_session';

const writeCookie = (value: string, maxAge: number) => {
    document.cookie = `${KEY}=${value};path=/;max-age=${maxAge};SameSite=Lax`;
    // Shared across WCP ↔ MLP on *.stctv.com
    if (location.hostname.includes('stctv.com')) {
        document.cookie = `${KEY}=${value};path=/;domain=.stctv.com;max-age=${maxAge};SameSite=Lax`;
    }
};

/** After stopSession — next page forces a new RUM session. */
export const markDataDogSessionForRenew = () => writeCookie('1', 120);

/**
 * After init: docs say not-granted → granted creates a new session when none is active.
 * https://docs.datadoghq.com/real_user_monitoring/application_monitoring/browser/advanced_configuration/#user-tracking-consent
 */
export const renewDataDogSessionIfNeeded = () => {
    if (!document.cookie.split(';').some((c) => c.trim().startsWith(`${KEY}=`))) {
        return;
    }
    writeCookie('', 0);
    datadogRum.setTrackingConsent('not-granted');
    datadogRum.setTrackingConsent('granted');
};

/**
 * Chrome raises this intervention once per `touchmove` while bitmovin-player-ui's seekbar
 * drags on a touch device, because it calls `preventDefault` in document-level listeners
 * that Chrome forces to be passive. Seeking still works, so the reports are pure noise.
 */
const PASSIVE_PREVENT_DEFAULT_INTERVENTION = 'Unable to preventDefault inside passive event listener';

/**
 * The same seekbar calls `preventDefault` on `touchend`; when the touch ends mid-scroll the
 * event is not cancelable and Chrome reports `IgnoredEventCancel`. Also harmless noise.
 */
const IGNORED_TOUCH_CANCEL_INTERVENTION = 'Ignored attempt to cancel a touch';

/**
 * Browsers replace errors thrown by cross-origin classic scripts (GTM tags, Genesys, extensions)
 * with a bare "Script error." and no file, line or stack, so the event cannot be attributed or
 * acted on. Datadog reports it as `Uncaught "Script error."` because no Error object is passed.
 */
const isOpaqueCrossOriginError = (message = '') => /^(?:Uncaught\s+)?"?Script error\.?"?$/.test(message.trim());

/**
 * Bitmovin's `player.destroy()` resets the view mode to inline, calling `document.exitFullscreen()`
 * when it still believes it is fullscreen. If the fullscreen container was already removed (channel
 * switch or route change while fullscreen), the browser has exited fullscreen itself, so Chromium
 * rejects that redundant call. The promise is internal to Bitmovin and the user sees no effect.
 */
const EXIT_FULLSCREEN_NOT_ACTIVE = "Failed to execute 'exitFullscreen' on 'Document': Document not active";

/**
 * A script injected by in-app browsers (seen in the Google iOS app) reads `.innerText` from
 * `#minisub-container ... span` on every click. That element never exists in WCP, so the read
 * always throws. iOS WebKit attributes the injected frame to the page URL, so it looks
 * first-party and `isThrownByNonAppScript` cannot catch it; match the selector instead.
 */
const INJECTED_MINISUB_SELECTOR = '#minisub-container';

/**
 * Browser AbortError when `HTMLMediaElement.play()` is cancelled by a later `pause()` (Bitmovin
 * UI toggle, ads, or tab visibility). Playback is fine; the rejection is noise. Also filtered at
 * the source in WCP's `guardPlayerApi` / `safePlayerPlay`; this is the RUM safety net.
 */
const PLAY_INTERRUPTED_BY_PAUSE = 'The play() request was interrupted by a call to pause()';

/**
 * WebKit words the same interrupted `play()` rejection generically, and it reaches RUM with no
 * frames because the promise is created inside the player. Only the frameless form is dropped:
 * an AbortError raised by our own code carries a stack and is still reported.
 */
const isFramelessWebKitAbortError = (message = '', stack = '') =>
    /^(?:Uncaught\s+)?"?AbortError: The operation was aborted\.?"?$/.test(message.trim()) &&
    !stack.split('\n').some((line) => /^\s*at\s|@/.test(line));

/**
 * The server CSP sets `worker-src blob:` only, so the Braze SDK's attempt to register
 * `/braze-service-worker.js` (web push) is blocked and Chrome reports a CSP violation. The page
 * works without it; allowing push needs `worker-src 'self' blob:` on the server, not app code.
 */
const isBrazeServiceWorkerCspViolation = (message = '') =>
    message.includes("blocked by 'worker-src'") && message.includes('braze-service-worker');

/**
 * API data (operator / payment-method `imageUrl`s on `mediaimage.intigral-ott.net`) still returns
 * some `http://` image URLs. The server CSP only allows `https:` images, so the browser blocks
 * them as mixed content and reports an `img-src` violation for every render.
 */
const isInsecureImageCspViolation = (message = '') =>
    message.includes("blocked by 'img-src'") && message.includes("'http://");

/**
 * Neither the app nor Bitmovin / Datadog load `blob:` images (their `blob:` URLs are workers and
 * media, allowed by `worker-src` / `media-src`). These come from extensions or injected tags.
 */
const isBlobImageCspViolation = (message = '') => message.includes("'blob' blocked by 'img-src'");

/**
 * Meta's in-app browsers (Instagram / Facebook on Android) inject scripts that issue a request the
 * browser records with the blocked URI `properties`. It isn't a URL the app ever requests.
 */
const isInAppBrowserConnectCspViolation = (message = '') =>
    message.includes("'properties' blocked by 'connect-src'");

/**
 * WebKit reports the blocked URI as `null` when it won't expose it (opaque origin or a cross-origin
 * redirect), typically for iframes created by marketing tags on campaign landing pages. There's no
 * URL to act on, so the report carries no signal.
 */
const isOpaqueFrameCspViolation = (message = '') => message.includes("'null' blocked by 'frame-src'");

/**
 * Service-worker registration blocked by the CSP `worker-src`, as each browser words it.
 * Firefox: "ServiceWorkerContainer.register: Script URL is not allowed by policy."
 */
const isServiceWorkerRegistrationBlocked = (message = '', stack = '') =>
    message.includes('Failed to register a ServiceWorker') ||
    message.includes('ServiceWorkerContainer.register: Script URL is not allowed by policy') ||
    (message.includes('The operation is insecure') && /\bregister\b/.test(stack));

/**
 * Third-party script errors are reported as unhandled `source` errors because our frames
 * sit underneath the throwing listener. Drop them rather than attribute them to the app.
 */
export const dataDogBeforeSend: RumBeforeSend = (event, _context) => {
    if (event.type !== 'error') {
        return true;
    }
    if (event.error.source === 'source' && isThrownByNonAppScript(event.error.stack)) {
        return false;
    }
    if (event.error.source === 'source' && isOpaqueCrossOriginError(event.error.message)) {
        return false;
    }
    if (event.error.message?.includes(EXIT_FULLSCREEN_NOT_ACTIVE)) {
        return false;
    }
    if (event.error.source === 'source' && event.error.message?.includes(INJECTED_MINISUB_SELECTOR)) {
        return false;
    }
    if (event.error.message?.includes(PLAY_INTERRUPTED_BY_PAUSE)) {
        return false;
    }
    if (event.error.source === 'source' && isFramelessWebKitAbortError(event.error.message, event.error.stack)) {
        return false;
    }
    if (
        event.error.source === 'report' &&
        (event.error.message?.includes(PASSIVE_PREVENT_DEFAULT_INTERVENTION) ||
            event.error.message?.includes(IGNORED_TOUCH_CANCEL_INTERVENTION))
    ) {
        return false;
    }
    if (event.error.source === 'report' && isBrazeServiceWorkerCspViolation(event.error.message)) {
        return false;
    }
    if (event.error.source === 'report' && isInsecureImageCspViolation(event.error.message)) {
        return false;
    }
    if (event.error.source === 'report' && isBlobImageCspViolation(event.error.message)) {
        return false;
    }
    if (event.error.source === 'report' && isInAppBrowserConnectCspViolation(event.error.message)) {
        return false;
    }
    if (event.error.source === 'report' && isOpaqueFrameCspViolation(event.error.message)) {
        return false;
    }
    if (isServiceWorkerRegistrationBlocked(event.error.message, event.error.stack)) {
        return false;
    }
    // Datadog also picks up console.error. Drop expected player noise.
    if (event.error.message?.includes('Error while playing')) {
        return false;
    }
    return true;
};
